// Shared (server + client safe) logic for the multi-surface "view switcher".
//
// HR Docket has three surfaces, each its own layout that historically bounced
// every other role away:
//   - admin    → /        (platform admin, on the admin dashboard's origin)
//   - user     → /        (HR workspace, on the customer app's origin)
//   - employee → /portal  (employee self-service, on the customer app's origin)
//
// A single account can legitimately reach more than one of these: an admin is
// also an HR user; a login that belongs to a company as staff (any `member`
// row — owner/admin/member/viewer) gets the HR workspace even if it was born an
// employee; and any login *linked to an employee record* (see the backend
// `employee_account` table + `requireEmployee`) can also use the portal. The
// view switcher lets such accounts move between the surfaces; a `hr_view` cookie
// records the chosen surface and the layouts honor it.
//
// Allowed views are still derived from real capabilities on every server
// render, so the cookie is a preference — never a privilege. A non-admin can
// never resolve the "admin" view no matter what the cookie says, and a login
// with no company membership can never resolve the "user" view.

export type ViewMode = "admin" | "user" | "employee";

/** Cookie that records the account's active surface. Client-readable on purpose. */
export const VIEW_COOKIE = "hr_view";

/** Landing route for each surface. */
export const VIEW_HOME: Record<ViewMode, string> = {
  admin: "/",
  user: "/",
  employee: "/portal",
};

/** Every surface, in switcher order. */
export const VIEW_MODES: ViewMode[] = ["admin", "user", "employee"];

/** Human-facing copy for the switcher menu. */
export const VIEW_META: Record<ViewMode, { label: string; description: string }> = {
  admin: { label: "Admin", description: "Platform administration" },
  user: { label: "HR Workspace", description: "Manage your team & documents" },
  employee: { label: "Employee", description: "Your self-service portal" },
};

/** Raw capability flags returned by `GET /api/account/views`. */
export type ViewCapabilities = {
  isAdmin: boolean;
  /**
   * Platform staff below admin: reaches the admin surface but only the
   * dashboard sections listed in `moderatorSections` (see
   * `lib/moderator-sections.ts`).
   */
  isModerator: boolean;
  moderatorSections: string[];
  isEmployeeRole: boolean;
  hasEmployeeProfile: boolean;
  /**
   * The login belongs to ≥1 company as staff (owner/admin/member/viewer). This —
   * not `!isEmployeeRole` — is what unlocks the HR Workspace surface, so a login
   * that was created as an employee can still be HR/staff of a company it was
   * invited to. A pure portal employee has no membership and stays out.
   */
  hasWorkspaceMembership: boolean;
  /** The companies the login is staff of, for the workspace switcher. */
  memberships: WorkspaceMembership[];
};

/** One company a login is staff of, surfaced to the workspace switcher. */
export type WorkspaceMembership = {
  organizationId: string;
  role: string;
  companyName: string;
};

/**
 * The surfaces this account may switch between, in display order. Derived purely
 * from capabilities so it is the single source of truth on both server and
 * client.
 */
export function allowedViews(cap: ViewCapabilities): ViewMode[] {
  const views: ViewMode[] = [];
  if (cap.isAdmin || cap.isModerator) views.push("admin");
  // Belonging to any company as staff unlocks the workspace — regardless of the
  // login's role. A pure portal employee (no membership) never gets it.
  if (cap.hasWorkspaceMembership) views.push("user");
  if (cap.hasEmployeeProfile) views.push("employee");
  return views;
}

/** The surface to fall back to when no (valid) cookie is set. */
export function defaultView(cap: ViewCapabilities): ViewMode {
  // A login with an employee profile lands on the portal by default (its primary
  // identity); when it also holds company membership it can switch to the HR
  // workspace, and the `hr_view` cookie then remembers that choice.
  if (cap.hasEmployeeProfile) return "employee";
  if (cap.isAdmin || cap.isModerator) return "admin";
  return "user";
}

/**
 * Resolves the account's active surface: the cookie's value when the account is
 * actually allowed it, otherwise the role default. This is what each layout
 * compares against to decide whether to render or redirect.
 */
export function resolveActiveView(
  cookieValue: string | undefined,
  cap: ViewCapabilities,
): ViewMode {
  const allowed = allowedViews(cap);
  if (cookieValue && allowed.includes(cookieValue as ViewMode)) {
    return cookieValue as ViewMode;
  }
  return defaultView(cap);
}

/**
 * The surface to render *within one app*, or `null` when the running app hosts
 * nothing this account may use.
 *
 * This is what the route guards resolve against, and it is deliberately
 * narrower than {@link resolveActiveView}: an account never leaves the app it
 * signed in on. `served` is the set of surfaces the running app actually hosts
 * (see `servedViews()` in `surfaces.ts`), so the `hr_view` cookie can only
 * select between the surfaces *this* app serves — an admin working in the HR
 * workspace is not dragged to the admin dashboard, and a login that isn't
 * platform staff resolves to `null` on the admin dashboard rather than being
 * bounced to the customer app.
 *
 * The one account with no *granted* surface here that still gets one is on an
 * app that serves the HR workspace: the workspace degrades per resource
 * (its APIs re-check membership and return empty or 403) rather than gating at
 * the door, so landing there beats having nowhere to land. An app that does not
 * serve the workspace — the admin dashboard — returns `null` instead. A portal
 * login is the exception to that exception: with no usable link (revoked, or
 * only invites still to accept) it is sent to the portal, never to an empty
 * workspace that would offer to set up a company of its own.
 */
export function localView(
  cookieValue: string | undefined,
  cap: ViewCapabilities,
  served: ViewMode[],
): ViewMode | null {
  const allowed = allowedViews(cap).filter((view) => served.includes(view));
  const [fallback] = allowed;
  if (fallback === undefined) {
    if (cap.isEmployeeRole) return served.includes("employee") ? "employee" : null;
    return served.includes("user") ? "user" : null;
  }
  if (cookieValue && allowed.includes(cookieValue as ViewMode)) {
    return cookieValue as ViewMode;
  }
  const preferred = defaultView(cap);
  return allowed.includes(preferred) ? preferred : fallback;
}
